import React, { useState, useEffect, useRef } from 'react';
import {
  Save, X, AlertCircle, Users, UserPlus,
  Search, FileText, CheckCircle, Loader,
  ChevronRight, Briefcase, Calendar, Phone, Mail, Check
} from 'lucide-react';
import SearchableSelect from '../../shared/SearchableSelect';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('dths_token')?.trim()}`,
  'Content-Type': 'application/json',
});

const inputStyle = {
  width: '100%', padding: '0.625rem',
  border: '1px solid #d1d5db', borderRadius: '6px',
  fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: '0.875rem',
  fontWeight: '500', color: '#374151', marginBottom: '0.5rem',
};

const sectionTitle = {
  fontSize: '1rem', fontWeight: '600', color: '#1f2937',
  marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e5e7eb',
};

const FORM_INICIAL = {
  nombre: '', apellido: '', cedula: '', email: '',
  telefono: '', direccion: '', fechaNacimiento: '',
  genero: 'Masculino', estadoCivil: 'Soltero',
  cargo: '', departamentoId: '', salario: '',
  fechaIngreso: '', tipoContrato: 'Indefinido',
  jornada: 'Tiempo Completo', turno: 'Diurno',
  supervisorId: '', justificacion: '',
};

/* ════════════════════════════════════════════════════════
   Sub-componente: Modal lista de postulantes
════════════════════════════════════════════════════════ */
const ModalPostulantes = ({ onSeleccionar, onClose }) => {
  const [postulantes, setPostulantes]   = useState([]);
  const [filtrados,   setFiltrados]     = useState([]);
  const [cargando,    setCargando]      = useState(true);
  const [busqueda,    setBusqueda]      = useState('');

  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    if (!busqueda.trim()) { setFiltrados(postulantes); return; }
    const t = busqueda.toLowerCase();
    setFiltrados(postulantes.filter(p =>
      p.nombreCompleto?.toLowerCase().includes(t) ||
      p.cedula?.includes(t) ||
      p.email?.toLowerCase().includes(t) ||
      p.cargoPostulado?.toLowerCase().includes(t) ||
      p.vacante?.toLowerCase().includes(t)
    ));
  }, [busqueda, postulantes]);

  const cargar = async () => {
    try {
      setCargando(true);
      // Traer todos (q=a para que devuelva una lista amplia)
      const r = await fetch(
        `${API_URL}/acciones-personal/postulantes?q=aa`,
        { headers: authHeaders() }
      );
      const d = await r.json();
      if (d.success) {
        setPostulantes(d.postulantes || []);
        setFiltrados(d.postulantes || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  };

  const estadoColor = (estado) => {
    const m = {
      'Aprobado':    { bg: '#d1fae5', color: '#065f46' },
      'Seleccionado':{ bg: '#dbeafe', color: '#1e40af' },
      'En Proceso':  { bg: '#fef3c7', color: '#92400e' },
      'Pendiente':   { bg: '#f3f4f6', color: '#374151' },
    };
    return m[estado] || { bg: '#f3f4f6', color: '#374151' };
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: '1rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: '14px',
        width: '100%', maxWidth: '780px',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
          color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
              Seleccionar Postulante
            </h3>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', opacity: 0.85 }}>
              {filtrados.length} postulante(s) disponibles · haz clic para seleccionar
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none',
            borderRadius: '8px', padding: '0.5rem', cursor: 'pointer',
            color: '#fff', lineHeight: 0,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Buscador */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{
              position: 'absolute', left: '0.75rem',
              top: '50%', transform: 'translateY(-50%)', color: '#9ca3af',
            }} />
            <input
              type="text"
              autoFocus
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Filtrar por nombre, cédula, email o cargo..."
              style={{
                ...inputStyle,
                paddingLeft: '2.25rem',
                border: '1px solid #c4b5fd',
              }}
            />
          </div>
        </div>

        {/* Lista */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {cargando ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              <Loader size={28} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 0.75rem' }} />
              <p style={{ margin: 0 }}>Cargando postulantes...</p>
            </div>
          ) : filtrados.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
              <Users size={40} style={{ margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ margin: 0 }}>No se encontraron postulantes</p>
            </div>
          ) : (
            filtrados.map((p, idx) => {
              const ec = estadoColor(p.estadoPostulacion);
              return (
                <div
                  key={p.postulacionId}
                  onClick={() => onSeleccionar(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                    background: idx % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: '700', fontSize: '1rem',
                  }}>
                    {p.nombre?.[0]}{p.apellido?.[0]}
                  </div>

                  {/* Info principal */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '700', color: '#111827', fontSize: '0.9rem' }}>
                        {p.nombreCompleto}
                      </span>
                      {p.estadoPostulacion && (
                        <span style={{
                          background: ec.bg, color: ec.color,
                          padding: '0.1rem 0.55rem', borderRadius: '9999px',
                          fontSize: '0.68rem', fontWeight: '600',
                        }}>
                          {p.estadoPostulacion}
                        </span>
                      )}
                      {p.totalDocumentos > 0 && (
                        <span style={{
                          background: '#ede9fe', color: '#6d28d9',
                          padding: '0.1rem 0.5rem', borderRadius: '9999px',
                          fontSize: '0.68rem', fontWeight: '500',
                          display: 'flex', alignItems: 'center', gap: '0.2rem',
                        }}>
                          <FileText size={10} />
                          {p.totalDocumentos} docs
                        </span>
                      )}
                    </div>

                    <div style={{
                      display: 'flex', gap: '1rem', marginTop: '0.3rem',
                      fontSize: '0.75rem', color: '#6b7280', flexWrap: 'wrap',
                    }}>
                      {p.cedula && (
                        <span>CI: {p.cedula}</span>
                      )}
                      {p.email && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                          <Mail size={11} />{p.email}
                        </span>
                      )}
                      {p.telefono && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                          <Phone size={11} />{p.telefono}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                      {p.cargoPostulado && (
                        <span style={{
                          background: '#f5f3ff', color: '#6d28d9',
                          padding: '0.15rem 0.6rem', borderRadius: '9999px',
                          fontSize: '0.7rem', fontWeight: '600',
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                        }}>
                          <Briefcase size={10} />{p.cargoPostulado}
                        </span>
                      )}
                      {p.departamento && (
                        <span style={{
                          background: '#eff6ff', color: '#1d4ed8',
                          padding: '0.15rem 0.6rem', borderRadius: '9999px',
                          fontSize: '0.7rem',
                        }}>
                          {p.departamento}
                        </span>
                      )}
                      {p.experienciaAnios > 0 && (
                        <span style={{
                          background: '#f0fdf4', color: '#166534',
                          padding: '0.15rem 0.6rem', borderRadius: '9999px',
                          fontSize: '0.7rem',
                        }}>
                          {p.experienciaAnios} años exp.
                        </span>
                      )}
                      {p.expectativaSalarial && (
                        <span style={{
                          background: '#fff7ed', color: '#c2410c',
                          padding: '0.15rem 0.6rem', borderRadius: '9999px',
                          fontSize: '0.7rem',
                        }}>
                          RD$ {parseFloat(p.expectativaSalarial).toLocaleString('es-DO')}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight size={18} style={{ color: '#c4b5fd', flexShrink: 0 }} />
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   Componente principal: FormularioIngreso
════════════════════════════════════════════════════════ */
const FormularioIngreso = ({ tipo, onClose, onSuccess }) => {
  const [departamentos, setDepartamentos]   = useState([]);
  const [supervisores,  setSupervisores]    = useState([]);
  const [loading,       setLoading]         = useState(false);
  const [mensaje,       setMensaje]         = useState({ tipo: '', texto: '' });
  const [formData,      setFormData]        = useState(FORM_INICIAL);

  const [modo,                setModo]                = useState('manual');
  const [mostrarLista,        setMostrarLista]        = useState(false);
  const [seleccionado,        setSeleccionado]        = useState(null);
  const [documentos,          setDocumentos]          = useState([]);
  const [docsSeleccionados,   setDocsSeleccionados]   = useState([]);

  useEffect(() => { cargarCatalogos(); }, []);

  const cargarCatalogos = async () => {
  try {
    const [dR, eR] = await Promise.all([
      fetch(`${API_URL}/departamentos`, { headers: authHeaders() }),
      fetch(`${API_URL}/empleados`,     { headers: authHeaders() }),
    ]);
    const dD = await dR.json();
    const eD = await eR.json();

    // Departamentos — usa success si existe, si no busca el array directamente
    const deptsArr = dD.departamentos || dD.recordset || (Array.isArray(dD) ? dD : []);
    setDepartamentos(deptsArr);

    // Empleados — el endpoint devuelve { empleados: [...] } sin success
    const empsArr = eD.empleados || eD.recordset || (Array.isArray(eD) ? eD : []);
    setSupervisores(
      empsArr.filter(e => e.Estado === 1 || e.ESTADO === 1)
    );

  } catch (err) {
    console.error('Error cargando catálogos:', err);
  }
};

  /* ── Seleccionar desde la lista ── */
  const seleccionarPostulante = async (p) => {
    setMostrarLista(false);
    setSeleccionado(p);

    setFormData(prev => ({
      ...prev,
      nombre:          p.nombre        || '',
      apellido:        p.apellido      || '',
      cedula:          p.cedula        || '',
      email:           p.email         || '',
      telefono:        p.telefono      || '',
      direccion:       p.direccion     || '',
      fechaNacimiento: p.fechaNacimiento ? p.fechaNacimiento.substring(0, 10) : '',
      genero:          p.genero        || 'Masculino',
      estadoCivil:     p.estadoCivil   || 'Soltero',
      cargo:           p.cargoPostulado || prev.cargo,
      salario:         p.expectativaSalarial ? String(p.expectativaSalarial) : prev.salario,
    }));

    await cargarDocumentos(p.postulacionId);
  };

  const cargarDocumentos = async (postulacionId) => {
    try {
      const r = await fetch(
        `${API_URL}/acciones-personal/postulantes/${postulacionId}/documentos`,
        { headers: authHeaders() }
      );
      const d = await r.json();
      if (d.success) {
        setDocumentos(d.documentos || []);
        setDocsSeleccionados(d.documentos.map(doc => doc.id));
      }
    } catch (err) {
      setDocumentos([]);
    }
  };

  const limpiarPostulante = () => {
    setSeleccionado(null);
    setDocumentos([]);
    setDocsSeleccionados([]);
    setFormData(FORM_INICIAL);
  };

  const toggleDoc = (id) =>
    setDocsSeleccionados(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );

  const cambiarModo = (m) => {
    setModo(m);
    if (m === 'manual') limpiarPostulante();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validar = () => {
    if (!formData.nombre || !formData.apellido || !formData.cedula)
      return setMensaje({ tipo: 'error', texto: 'Nombre, apellido y cédula son obligatorios' }), false;
    if (!formData.email || !formData.telefono)
      return setMensaje({ tipo: 'error', texto: 'Email y teléfono son obligatorios' }), false;
    if (!formData.cargo || !formData.departamentoId)
      return setMensaje({ tipo: 'error', texto: 'Cargo y departamento son obligatorios' }), false;
    if (!formData.salario || parseFloat(formData.salario) <= 0)
      return setMensaje({ tipo: 'error', texto: 'Debe especificar un salario válido' }), false;
    if (!formData.fechaIngreso)
      return setMensaje({ tipo: 'error', texto: 'Debe especificar la fecha de ingreso' }), false;
    if (!formData.justificacion)
      return setMensaje({ tipo: 'error', texto: 'Debe incluir una justificación' }), false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;

    try {
      setLoading(true);
      setMensaje({ tipo: '', texto: '' });

      const payload = {
        tipoAccion:         tipo,
        empleadoId:         null,
        fechaEfectiva:      formData.fechaIngreso,
        justificacion:      formData.justificacion,
        postulacionId:      seleccionado?.postulacionId || null,
        documentosImportar: docsSeleccionados,
        datosNuevos: {
          nombre:          formData.nombre,
          apellido:        formData.apellido,
          cedula:          formData.cedula,
          email:           formData.email,
          telefono:        formData.telefono,
          direccion:       formData.direccion,
          fechaNacimiento: formData.fechaNacimiento || null,
          genero:          formData.genero,
          estadoCivil:     formData.estadoCivil,
          cargo:           formData.cargo,
          departamentoId:  parseInt(formData.departamentoId),
          salario:         parseFloat(formData.salario),
          fechaIngreso:    formData.fechaIngreso,
          tipoContrato:    formData.tipoContrato,
          jornada:         formData.jornada,
          turno:           formData.turno,
          supervisorId:    formData.supervisorId ? parseInt(formData.supervisorId) : null,
        },
      };

      const res  = await fetch(`${API_URL}/acciones-personal`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        const docsMsg = data.documentosImportados > 0
          ? ` Se importaron ${data.documentosImportados} documento(s).` : '';
        setMensaje({ tipo: 'success', texto: `Acción creada exitosamente.${docsMsg}` });
        setTimeout(() => { onSuccess(); onClose(); }, 2000);
      } else {
        setMensaje({ tipo: 'error', texto: data.message || 'Error al crear la acción' });
      }
    } catch (err) {
      setMensaje({ tipo: 'error', texto: 'Error al crear la acción de personal' });
    } finally {
      setLoading(false);
    }
  };

  /* ─── RENDER ─────────────────────────────────────────── */
  return (
    <>
      <form onSubmit={handleSubmit}>

        {/* Mensaje */}
        {mensaje.texto && (
          <div style={{
            padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem',
            background: mensaje.tipo === 'success' ? '#d1fae5' : '#fee2e2',
            color: mensaje.tipo === 'success' ? '#065f46' : '#991b1b',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            <AlertCircle size={18} />
            <span>{mensaje.texto}</span>
          </div>
        )}

        {/* ══ TOGGLE MODO ════════════════════════════════ */}
        <div style={{
          marginBottom: '2rem', background: '#f8fafc',
          border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem',
        }}>
          <p style={{
            fontSize: '0.7rem', fontWeight: '700', color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem',
          }}>
            Fuente de datos del empleado
          </p>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {[
              { key: 'manual',     label: 'Ingreso manual',   Icon: UserPlus, activeColor: '#3b82f6', activeBg: '#eff6ff', activeBorder: '#3b82f6' },
              { key: 'postulante', label: 'Desde postulante', Icon: Users,    activeColor: '#7c3aed', activeBg: '#f5f3ff', activeBorder: '#8b5cf6' },
            ].map(({ key, label, Icon, activeColor, activeBg, activeBorder }) => (
              <button key={key} type="button" onClick={() => cambiarModo(key)} style={{
                flex: 1, padding: '0.75rem 1rem', borderRadius: '8px',
                border: `2px solid ${modo === key ? activeBorder : '#e2e8f0'}`,
                background: modo === key ? activeBg : '#fff',
                color: modo === key ? activeColor : '#6b7280',
                fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '0.5rem', transition: 'all 0.15s ease',
              }}>
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>

          {/* ── Panel modo postulante ── */}
          {modo === 'postulante' && (
            <div style={{ marginTop: '1rem' }}>

              {/* Sin selección → botón abrir lista */}
              {!seleccionado && (
                <button
                  type="button"
                  onClick={() => setMostrarLista(true)}
                  style={{
                    width: '100%', padding: '0.875rem 1rem',
                    background: '#fff', border: '2px dashed #c4b5fd',
                    borderRadius: '10px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.6rem', color: '#7c3aed', fontWeight: '600',
                    fontSize: '0.9rem', transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff'; e.currentTarget.style.borderColor = '#8b5cf6'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                >
                  <Users size={18} />
                  Ver listado de postulantes
                  <ChevronRight size={16} />
                </button>
              )}

              {/* Con selección → tarjeta del postulante */}
              {seleccionado && (
                <div style={{
                  background: '#fff', border: '2px solid #a78bfa',
                  borderRadius: '10px', padding: '1rem', position: 'relative',
                }}>
                  <button
                    type="button"
                    onClick={limpiarPostulante}
                    style={{
                      position: 'absolute', top: '0.75rem', right: '0.75rem',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: '#9ca3af', padding: '0.25rem', lineHeight: 0,
                    }}
                    title="Cambiar postulante"
                  >
                    <X size={16} />
                  </button>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: '700', fontSize: '1rem',
                    }}>
                      {seleccionado.nombre?.[0]}{seleccionado.apellido?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', color: '#111827', fontSize: '0.95rem' }}>
                        {seleccionado.nombreCompleto}
                        <CheckCircle size={15} style={{ color: '#7c3aed', marginLeft: '0.4rem', verticalAlign: 'middle' }} />
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.2rem' }}>
                        {seleccionado.email}
                        {seleccionado.cedula && ` · CI: ${seleccionado.cedula}`}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.4rem' }}>
                        {seleccionado.cargoPostulado && (
                          <span style={{ background: '#f5f3ff', color: '#6d28d9', padding: '0.15rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '600' }}>
                            {seleccionado.cargoPostulado}
                          </span>
                        )}
                        {seleccionado.departamento && (
                          <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '0.15rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem' }}>
                            {seleccionado.departamento}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Cambiar postulante */}
                  <button
                    type="button"
                    onClick={() => setMostrarLista(true)}
                    style={{
                      marginTop: '0.75rem', width: '100%',
                      padding: '0.4rem', background: '#f5f3ff',
                      border: '1px solid #ddd6fe', borderRadius: '6px',
                      color: '#6d28d9', fontSize: '0.78rem', fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Cambiar postulante
                  </button>

                  {/* Documentos */}
                  {documentos.length > 0 && (
                    <div style={{ marginTop: '0.875rem', paddingTop: '0.875rem', borderTop: '1px solid #ede9fe' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <p style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                          Documentos a importar ({documentos.length})
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button type="button" onClick={() => setDocsSeleccionados(documentos.map(d => d.id))}
                            style={{ fontSize: '0.7rem', color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer' }}>
                            Todos
                          </button>
                          <button type="button" onClick={() => setDocsSeleccionados([])}
                            style={{ fontSize: '0.7rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>
                            Ninguno
                          </button>
                        </div>
                      </div>
                      {documentos.map(doc => (
                        <label key={doc.id} style={{
                          display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer',
                          padding: '0.4rem 0.5rem', borderRadius: '6px',
                          background: docsSeleccionados.includes(doc.id) ? '#f5f3ff' : 'transparent',
                          marginBottom: '0.2rem',
                        }}>
                          <input type="checkbox" checked={docsSeleccionados.includes(doc.id)}
                            onChange={() => toggleDoc(doc.id)}
                            style={{ accentColor: '#7c3aed', width: '13px', height: '13px' }} />
                          <FileText size={13} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '500' }}>{doc.tipoDocumento}</span>
                          <span style={{ fontSize: '0.72rem', color: '#9ca3af', marginLeft: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                            {doc.nombreArchivo}
                          </span>
                        </label>
                      ))}
                      {docsSeleccionados.length > 0 && (
                        <p style={{ fontSize: '0.72rem', color: '#7c3aed', marginTop: '0.4rem', marginBottom: 0 }}>
                          <Check size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />{docsSeleccionados.length} documento(s) se importarán
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ══ INFORMACIÓN PERSONAL ═══════════════════════ */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={sectionTitle}>Información Personal</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Nombre <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Apellido <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" name="apellido" value={formData.apellido} onChange={handleChange} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Cédula <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" name="cedula" value={formData.cedula} onChange={handleChange} placeholder="000-0000000-0" required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Fecha de Nacimiento</label>
              <input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleChange} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Género</label>
              <select name="genero" value={formData.genero} onChange={handleChange} style={inputStyle}>
                <option value="Masculino">Masculino</option>
                <option value="Femenino">Femenino</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Estado Civil</label>
              <select name="estadoCivil" value={formData.estadoCivil} onChange={handleChange} style={inputStyle}>
                <option value="Soltero">Soltero(a)</option>
                <option value="Casado">Casado(a)</option>
                <option value="Divorciado">Divorciado(a)</option>
                <option value="Viudo">Viudo(a)</option>
                <option value="Union Libre">Unión Libre</option>
              </select>
            </div>
          </div>
        </div>

        {/* ══ INFORMACIÓN DE CONTACTO ════════════════════ */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={sectionTitle}>Información de Contacto</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Email <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Teléfono <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="(809) 000-0000" required style={inputStyle} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Dirección</label>
              <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* ══ INFORMACIÓN LABORAL ════════════════════════ */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={sectionTitle}>Información Laboral</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Cargo <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" name="cargo" value={formData.cargo} onChange={handleChange} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Departamento <span style={{ color: '#ef4444' }}>*</span></label>
              <SearchableSelect
                value={formData.departamentoId}
                onChange={v => setFormData(prev => ({ ...prev, departamentoId: v }))}
                options={departamentos.map(d => ({ id: d.DepartamentoID, label: d.Nombre }))}
                placeholder="Buscar departamento..."
                emptyLabel="Seleccionar departamento"
                inputStyle={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Supervisor</label>
              <SearchableSelect
                value={formData.supervisorId}
                onChange={v => setFormData(prev => ({ ...prev, supervisorId: v }))}
                options={supervisores.map(s => ({ id: s.EmpleadoID, label: `${s.NOMBRE} ${s.APELLIDO}` }))}
                placeholder="Buscar supervisor..."
                emptyLabel="Sin supervisor asignado"
                inputStyle={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Salario <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="number" name="salario" value={formData.salario} onChange={handleChange}
                placeholder={seleccionado?.expectativaSalarial ? `Expectativa: ${seleccionado.expectativaSalarial}` : '0.00'}
                step="0.01" required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Fecha de Ingreso <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="date" name="fechaIngreso" value={formData.fechaIngreso} onChange={handleChange} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Tipo de Contrato</label>
              <select name="tipoContrato" value={formData.tipoContrato} onChange={handleChange} style={inputStyle}>
                <option value="Indefinido">Indefinido</option>
                <option value="Temporal">Temporal</option>
                <option value="Por Proyecto">Por Proyecto</option>
                <option value="Prueba">Período de Prueba</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Jornada Laboral</label>
              <select name="jornada" value={formData.jornada} onChange={handleChange} style={inputStyle}>
                <option value="Tiempo Completo">Tiempo Completo</option>
                <option value="Medio Tiempo">Medio Tiempo</option>
                <option value="Por Horas">Por Horas</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Turno</label>
              <select name="turno" value={formData.turno} onChange={handleChange} style={inputStyle}>
                <option value="Diurno">Diurno (8am - 5pm)</option>
                <option value="Nocturno">Nocturno (8pm - 5am)</option>
                <option value="Rotativo">Rotativo</option>
                <option value="Mixto">Mixto</option>
              </select>
            </div>
          </div>
        </div>

        {/* ══ JUSTIFICACIÓN ══════════════════════════════ */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={labelStyle}>Justificación <span style={{ color: '#ef4444' }}>*</span></label>
          <textarea
            name="justificacion" value={formData.justificacion} onChange={handleChange}
            placeholder={
              seleccionado
                ? `Ingreso de ${seleccionado.nombreCompleto}, seleccionado mediante proceso de reclutamiento del portal Empleos ProDominicana para la vacante de ${seleccionado.cargoPostulado || '___'}...`
                : 'Explique el motivo de esta contratación...'
            }
            required rows={4}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* ══ BOTONES ════════════════════════════════════ */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '1rem',
          paddingTop: '1rem', borderTop: '1px solid #e5e7eb',
        }}>
          <button type="button" onClick={onClose} disabled={loading} style={{
            padding: '0.625rem 1.5rem', background: '#f3f4f6', color: '#374151',
            border: '1px solid #d1d5db', borderRadius: '8px',
            fontSize: '0.875rem', fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
          }}>
            Cancelar
          </button>

          <button type="submit" disabled={loading} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.625rem 1.5rem', border: 'none', borderRadius: '8px',
            fontSize: '0.875rem', fontWeight: '600', color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            background: loading ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            boxShadow: loading ? 'none' : '0 4px 6px rgba(59,130,246,0.3)',
          }}>
            {loading ? (
              <>
                <div style={{ width: '16px', height: '16px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Guardando...
              </>
            ) : (
              <>
                <Save size={18} />
                Crear Acción de Ingreso
                {docsSeleccionados.length > 0 && (
                  <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '9999px', padding: '0.1rem 0.5rem', fontSize: '0.72rem' }}>
                    +{docsSeleccionados.length} docs
                  </span>
                )}
              </>
            )}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </form>

      {/* Modal lista de postulantes (fuera del form) */}
      {mostrarLista && (
        <ModalPostulantes
          onSeleccionar={seleccionarPostulante}
          onClose={() => setMostrarLista(false)}
        />
      )}
    </>
  );
};

export default FormularioIngreso;